import type { Stadt } from './stadt';
import type { BedarfStatus } from './bedarf';
import type { GeoPrecision } from './kennenlernen';

/** The Bedarf a Bewerber applied to, as returned on the detail endpoint. */
export interface BeworbenAufBedarf {
  id: string;
  childFirstName: string;
  childLastName: string | null;
  schoolName: string | null;
}

/**
 * A position this Bewerber is assigned to. The FK lives on the Bedarf, so this
 * arrives as a list; fachlich there is at most one (the backend enforces it).
 */
export interface ZugeordneterBedarf extends BeworbenAufBedarf {
  status: string;
  zugeordnetAt: string | null;
}

// Bewerber Status enum matching backend
export enum BewerberStatus {
  NEU = 'NEU',
  KEINE_ANLAGEN = 'KEINE_ANLAGEN',
  ZU_KLAEREN = 'ZU_KLAEREN',
  NICHT_GEEIGNET = 'NICHT_GEEIGNET',
  GEEIGNET = 'GEEIGNET',
  EINGELADEN = 'EINGELADEN',
  VORSTELLUNG = 'VORSTELLUNG',
  IM_POOL = 'IM_POOL',
  KENNENLERNGESPRAECH = 'KENNENLERNGESPRAECH',
  ONBOARDING = 'ONBOARDING',
  GEWONNEN = 'GEWONNEN',
  OFFBOARDING = 'OFFBOARDING',
  VERLOREN = 'VERLOREN',
}

// Document type enum matching backend
export enum DokumentTyp {
  LEBENSLAUF = 'LEBENSLAUF',
  ZEUGNIS = 'ZEUGNIS',
  FUEHRUNGSZEUGNIS = 'FUEHRUNGSZEUGNIS',
  QUALIFIKATION = 'QUALIFIKATION',
  SONSTIGES = 'SONSTIGES',
}

// User reference (for createdBy, uploadedBy)
export interface UserRef {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

// Document interface
export interface Dokument {
  id: string;
  bewerberId: string;
  typ: DokumentTyp;
  originalFilename: string;
  storedFilename: string;
  mimeType: string;
  fileSize: number;
  uploadedById: string;
  uploadedAt: string;
  uploadedBy?: UserRef;
}

// Bewerber interface (full)
export interface Bewerber {
  id: string;

  // Personal data
  firstName: string;
  lastName: string;
  gender: string | null;
  email: string | null;
  phone: string | null;
  street: string | null;
  cityId: string | null;
  city: Stadt | null;
  postalCode: string | null;
  dateOfBirth: string | null;

  // Qualification & Education
  isFachkraft: boolean | null;
  bildungsabschluss: string | null;
  ausbildungsDauer: string | null;
  berufserfahrung: string | null;
  erfahrungMitKindern: boolean | null;

  // Mobility
  hasFuehrerschein: boolean | null;
  hasAuto: boolean | null;
  hasOepnv: boolean | null;
  maxPendelMinuten: number | null;
  maxPendelKm: number | null;

  // Availability & Compensation
  availableFrom: string | null;
  kuendigungsfrist: string | null;
  minWochenstunden: number | null;
  maxWochenstunden: number | null;
  geforderterStundensatz: number | null;
  angebotenerStundensatz: number | null;
  tvoedsueLevel: string | null;
  tvoedsueStufe: number | null;

  // Interview assessment (Fazit)
  geeignetFuer: string[];

  // Other
  ortStelleIds: string[];
  ortStellen: Stadt[];
  /** The concrete Bedarf this application refers to. */
  beworbenAufBedarfId: string | null;
  beworbenAufBedarf: BeworbenAufBedarf | null;
  zugeordneteBedarfe?: ZugeordneterBedarf[];
  status: BewerberStatus;
  qualifications: Record<string, unknown> | null;
  notes: string | null;

  // Confirmation tracking
  isConfirmed: boolean;
  confirmedAt: string | null;

  // Rejection tracking
  absageGrund: string | null;
  absageAt: string | null;

  // Nicht geeignet tracking
  nichtGeeignetGrund: string | null;
  nichtGeeignetAt: string | null;

  // Status tracking for reactivation
  previousStatus: BewerberStatus | null;
  wasEverGeeignet: boolean;

  // Visual markers
  isReopened: boolean;
  hasSecondApplication: boolean;

  // Attention marker
  isMarked: boolean;
  markedAt: string | null;

  /**
   * Wann der Bewerber zuletzt eingestellt wurde; null bei Altdaten ohne
   * Audit-Spur und in jedem anderen Status.
   */
  gewonnenAt: string | null;

  /** Wann der Bewerber in seinen aktuellen Status kam. Ordnet die Kanban-Spalten. */
  statusChangedAt: string;

  // Einstellen-Checkliste
  onboardingUnterlagenGesendet: boolean;
  onboardingAvAbgelegt: boolean;

  // Stillgelegte Schritte: nicht mehr in der Checkliste, aber weiterhin auf
  // dem Datensatz -- der Nachweis fuer bereits eingestellte Mitarbeiter.
  onboardingArbeitsvertragVersendet: boolean;
  onboardingMitarbeiterKilankaAngelegt: boolean;
  onboardingMitarbeiterAngemeldet: boolean;
  onboardingOriginalAvAbgelegt: boolean;
  /** Calendar day (ISO). Documents only — moves no status, counts nowhere. */
  onboardingErsterArbeitstag: string | null;

  // Offboarding-Schritte
  offboardingElternKommuniziert: boolean;
  offboardingKostentraegerKommuniziert: boolean;
  offboardingKuendigungVersendet: boolean;
  offboardingAufhebungsvertragVersendet: boolean;
  offboardingWegfallZweckVersendet: boolean;
  offboardingBestaetigungAbgelegt: boolean;
  offboardingStundenGeklaert: boolean;
  offboardingKilankaOffboarding: boolean;
  offboardingMitarbeiterAbgemeldet: boolean;
  /** Calendar days (ISO). Until neither has passed, the person counts as eingesetzt. */
  offboardingLetzterArbeitstag: string | null;
  offboardingLetzterEinsatztag: string | null;

  // System fields
  assignedToId: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;

  // Relations
  assignedTo?: UserRef;
  createdBy?: UserRef;
  dokumente?: Dokument[];
  kennenlernen?: unknown[];
  zuordnungen?: unknown[];
  _count?: {
    kennenlernen: number;
    zuordnungen: number;
  };

  // Computed fields from list endpoint
  hasInvitationEmails?: boolean;
}

// Create Bewerber DTO
export interface CreateBewerberDto {
  // Personal data
  firstName: string;
  lastName: string;
  gender?: string;
  email?: string;
  phone?: string;
  street?: string;
  cityId?: string;
  postalCode?: string;
  dateOfBirth?: string;

  // Qualification & Education
  isFachkraft?: boolean;
  bildungsabschluss?: string;
  ausbildungsDauer?: string;
  berufserfahrung?: string;
  erfahrungMitKindern?: boolean;

  // Mobility
  hasFuehrerschein?: boolean;
  hasAuto?: boolean;
  hasOepnv?: boolean;
  maxPendelMinuten?: number;
  maxPendelKm?: number;

  // Availability & Compensation
  availableFrom?: string;
  kuendigungsfrist?: string;
  minWochenstunden?: number;
  maxWochenstunden?: number;
  geforderterStundensatz?: number;
  angebotenerStundensatz?: number;
  tvoedsueLevel?: string;
  tvoedsueStufe?: number;

  // Interview assessment (Fazit)
  geeignetFuer?: string[];

  // Other
  ortStelleIds?: string[];
  /** Pass null to clear the applied-for Stelle. */
  beworbenAufBedarfId?: string | null;
  status?: BewerberStatus;
  qualifications?: Record<string, unknown>;
  notes?: string;
}

// Update Bewerber DTO
export interface UpdateBewerberDto {
  // Personal data
  firstName?: string;
  lastName?: string;
  gender?: string;
  email?: string;
  phone?: string;
  street?: string;
  cityId?: string;
  postalCode?: string;
  dateOfBirth?: string;

  // Qualification & Education
  isFachkraft?: boolean;
  bildungsabschluss?: string;
  ausbildungsDauer?: string;
  berufserfahrung?: string;
  erfahrungMitKindern?: boolean;

  // Mobility
  hasFuehrerschein?: boolean;
  hasAuto?: boolean;
  hasOepnv?: boolean;
  maxPendelMinuten?: number;
  maxPendelKm?: number;

  // Availability & Compensation
  availableFrom?: string;
  kuendigungsfrist?: string;
  minWochenstunden?: number;
  maxWochenstunden?: number;
  geforderterStundensatz?: number;
  angebotenerStundensatz?: number;
  tvoedsueLevel?: string;
  tvoedsueStufe?: number;

  // Interview assessment (Fazit)
  geeignetFuer?: string[];

  // Einstellen-Checkliste. Beide zusammen auf true zu setzen, ohne einen
  // Status mitzuschicken, verschiebt den Bewerber im Backend auf GEWONNEN.
  onboardingUnterlagenGesendet?: boolean;
  onboardingAvAbgelegt?: boolean;

  // Stillgelegte Schritte: schreibbar, aber ohne Wirkung auf den Status.
  onboardingArbeitsvertragVersendet?: boolean;
  onboardingMitarbeiterKilankaAngelegt?: boolean;
  onboardingMitarbeiterAngemeldet?: boolean;
  onboardingOriginalAvAbgelegt?: boolean;
  /** ISO calendar day; null clears it. */
  onboardingErsterArbeitstag?: string | null;

  // Offboarding-Schritte
  offboardingElternKommuniziert?: boolean;
  offboardingKostentraegerKommuniziert?: boolean;
  offboardingKuendigungVersendet?: boolean;
  offboardingAufhebungsvertragVersendet?: boolean;
  offboardingWegfallZweckVersendet?: boolean;
  offboardingBestaetigungAbgelegt?: boolean;
  offboardingStundenGeklaert?: boolean;
  offboardingKilankaOffboarding?: boolean;
  offboardingMitarbeiterAbgemeldet?: boolean;
  /** ISO calendar day; null clears it. */
  offboardingLetzterArbeitstag?: string | null;
  offboardingLetzterEinsatztag?: string | null;

  // Other
  ortStelleIds?: string[];
  /** Pass null to clear the applied-for Stelle. */
  beworbenAufBedarfId?: string | null;
  status?: BewerberStatus;
  qualifications?: Record<string, unknown>;
  notes?: string;
  assignedToId?: string | null;
}

// Assign Bewerber DTO (for "Zur Klärung" action)
export interface AssignBewerberDto {
  userId: string;
  comment: string;
}

// Query parameters for listing
export interface QueryBewerberDto {
  search?: string;
  status?: BewerberStatus;
  city?: string;
  ortStelle?: string;
  assignedUserId?: string;
  page?: number;
  limit?: number;
  sortBy?: 'updatedAt' | 'createdAt' | 'statusChangedAt' | 'firstName' | 'lastName' | 'status';
  sortOrder?: 'asc' | 'desc';
}

// API response wrapper
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Bewerber statistics
export interface BewerberStatistics {
  total: number;
  byStatus: Record<string, number>;
  newThisMonth: number;
  /**
   * Everyone in GEWONNEN plus the ONBOARDING Bewerber whose Arbeitsvertrag is
   * already filed — the point from which someone is actually working, well
   * before the rest of the checklist is done — plus OFFBOARDING until one of
   * the two stichtage has passed.
   */
  eingesetzteSchulbegleiter: number;
  /** The OFFBOARDING share of eingesetzteSchulbegleiter. */
  eingesetztImOffboarding: number;
}

// Status display configuration
export interface BewerberStatusDisplay {
  label: string;
  color: string;
  bgColor: string;
  /**
   * Abweichende Beschriftung der Kanban-Spalte. Eine Spalte ist 220px breit
   * und traegt ihre Ueberschrift auch senkrecht, wenn sie eingeklappt ist --
   * dort passt ein anderer Zuschnitt als in ein Badge oder einen Filter.
   * Fehlt sie, gilt `label`.
   */
  kanbanLabel?: string;
}

export const BewerberStatusConfig: Record<BewerberStatus, BewerberStatusDisplay> = {
  [BewerberStatus.NEU]: { label: 'Neu', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  [BewerberStatus.KEINE_ANLAGEN]: { label: 'Keine Anlagen', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  [BewerberStatus.ZU_KLAEREN]: { label: 'Zu Klären', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  [BewerberStatus.NICHT_GEEIGNET]: { label: 'Nicht geeignet', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  [BewerberStatus.GEEIGNET]: { label: 'Geeignet', color: 'text-cyan-700', bgColor: 'bg-cyan-100' },
  [BewerberStatus.EINGELADEN]: { label: 'Eingeladen', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  [BewerberStatus.VORSTELLUNG]: { label: 'Vorstellung', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  [BewerberStatus.IM_POOL]: { label: 'Im Pool', color: 'text-green-700', bgColor: 'bg-green-100' },
  [BewerberStatus.KENNENLERNGESPRAECH]: { label: 'Kennenlerngespräch', color: 'text-lime-700', bgColor: 'bg-lime-100' },
  // Der Enum-Wert heisst weiter ONBOARDING -- Datenbank und API sind davon
  // unberuehrt, umbenannt ist nur, was ein Mensch liest.
  [BewerberStatus.ONBOARDING]: {
    label: 'Einstellen',
    kanbanLabel: 'Bew. einstellen',
    color: 'text-teal-700',
    bgColor: 'bg-teal-100',
  },
  [BewerberStatus.GEWONNEN]: { label: 'Gewonnen', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
  [BewerberStatus.OFFBOARDING]: { label: 'Offboarding', color: 'text-rose-700', bgColor: 'bg-rose-100' },
  [BewerberStatus.VERLOREN]: { label: 'Verloren', color: 'text-red-700', bgColor: 'bg-red-100' },
};

/**
 * The statuses in which a Bewerber takes part in Umkreis-Matching at all.
 * Mirrors MATCHING_ELIGIBLE_STATUSES in backend/src/modules/geo/geo.constants.ts:
 * only these addresses are geocoded, so for any other status a matching list
 * would forever report "wird noch verortet" for an address nobody will locate.
 * OFFBOARDING is included: someone leaving a position may take the next one.
 */
export const MATCHING_ELIGIBLE_STATUSES: BewerberStatus[] = [
  BewerberStatus.GEEIGNET,
  BewerberStatus.EINGELADEN,
  BewerberStatus.VORSTELLUNG,
  BewerberStatus.IM_POOL,
  BewerberStatus.KENNENLERNGESPRAECH,
  BewerberStatus.ONBOARDING,
  BewerberStatus.GEWONNEN,
  BewerberStatus.OFFBOARDING,
];

// Status order for Kanban
export const BEWERBER_STATUS_ORDER: BewerberStatus[] = [
  BewerberStatus.NEU,
  BewerberStatus.KEINE_ANLAGEN,
  BewerberStatus.ZU_KLAEREN,
  BewerberStatus.NICHT_GEEIGNET,
  BewerberStatus.GEEIGNET,
  BewerberStatus.EINGELADEN,
  BewerberStatus.VORSTELLUNG,
  BewerberStatus.IM_POOL,
  BewerberStatus.KENNENLERNGESPRAECH,
  BewerberStatus.ONBOARDING,
  BewerberStatus.GEWONNEN,
  BewerberStatus.OFFBOARDING,
  BewerberStatus.VERLOREN,
];

/**
 * Die Einstellen-Checkliste.
 *
 * Sie ist nicht blosse Anzeige: sind beide Schritte gehakt, schiebt das Backend
 * den Bewerber selbsttaetig auf GEWONNEN. Die Liste muss deshalb mit
 * ONBOARDING_STEP_FIELDS in backend/src/modules/bewerber/bewerber.service.ts
 * deckungsgleich bleiben -- ein Schritt, den nur eine Seite kennt, macht aus
 * der Automatik ein Raetsel.
 *
 * Vier weitere Schritte fallen erst nach der Einstellung an (Kilanka,
 * Anmeldung, Original-AV). Sie gaten nichts und loesen nichts aus, gehoeren
 * aber zur Liste, sobald der Bewerber eingestellt ist: ab GEWONNEN zeigt sie
 * alle sechs, siehe einstellenStepsFuer().
 */
export interface OnboardingStep {
  field: keyof Pick<
    Bewerber,
    | 'onboardingUnterlagenGesendet'
    | 'onboardingArbeitsvertragVersendet'
    | 'onboardingAvAbgelegt'
    | 'onboardingMitarbeiterKilankaAngelegt'
    | 'onboardingMitarbeiterAngemeldet'
    | 'onboardingOriginalAvAbgelegt'
  >;
  label: string;
}

const UNTERLAGEN_GESENDET: OnboardingStep = {
  field: 'onboardingUnterlagenGesendet',
  label: 'Unterlagen per Mail gesendet',
};
const AV_ABGELEGT: OnboardingStep = {
  field: 'onboardingAvAbgelegt',
  label: 'AV unterschrieben abgelegt',
};

/** Die zwei Schritte, die in ONBOARDING die Einstellung ausloesen. */
export const ONBOARDING_STEPS: OnboardingStep[] = [UNTERLAGEN_GESENDET, AV_ABGELEGT];

/** Die ganze Liste in der gewohnten Reihenfolge -- so steht sie ab GEWONNEN. */
export const EINSTELLEN_ALL_STEPS: OnboardingStep[] = [
  UNTERLAGEN_GESENDET,
  { field: 'onboardingArbeitsvertragVersendet', label: 'Arbeitsvertrag versendet (unterschrieben)' },
  AV_ABGELEGT,
  { field: 'onboardingMitarbeiterKilankaAngelegt', label: 'Mitarbeiter in Kilanka angelegt' },
  { field: 'onboardingMitarbeiterAngemeldet', label: 'Mitarbeiter angemeldet' },
  { field: 'onboardingOriginalAvAbgelegt', label: 'Original AV unterschrieben (abgelegt)' },
];

/**
 * Welche Einstellen-Schritte in diesem Status zu sehen sind. In ONBOARDING nur
 * die zwei, die weiterschieben -- ein Haken dort, der nichts ausloest, wuerde
 * die Automatik verschleiern. Danach die ganze Liste.
 */
export const einstellenStepsFuer = (status: BewerberStatus): OnboardingStep[] =>
  status === BewerberStatus.ONBOARDING ? ONBOARDING_STEPS : EINSTELLEN_ALL_STEPS;

// How many of the two Einstellen steps are still unchecked. Open steps no
// longer block GEWONNEN — they only make the move ask first.
export const offeneOnboardingSchritte = (bewerber: Bewerber): number =>
  ONBOARDING_STEPS.filter((step) => !bewerber[step.field]).length;

/**
 * Die drei Wartezustaende in ONBOARDING, je eine Kanban-Spalte. Abgeleitet,
 * nicht gespeichert: ein eigenes Feld koennte dem Bedarf-Status und den Haken
 * nur widersprechen.
 */
export type EinstellenPhase = 'KT_GO' | 'AV_ERSTELLEN' | 'UNTERSCHRIFT';

export const EINSTELLEN_PHASEN: Record<EinstellenPhase, { label: string; beschreibung: string }> = {
  KT_GO: {
    label: 'Warten auf KT-Go',
    beschreibung: 'Warten auf Kostenträger-Go für den Fall – der Arbeitsvertrag kann noch nicht versendet werden',
  },
  AV_ERSTELLEN: {
    label: 'AV erstellen',
    beschreibung: 'Arbeitsvertrag wird erstellt und kann versendet werden',
  },
  UNTERSCHRIFT: {
    label: 'Warten auf Unterschrift',
    beschreibung: 'Warten auf die digitale Unterschrift bzw. die Mail des Mitarbeiters',
  },
};

export const EINSTELLEN_PHASEN_REIHENFOLGE: EinstellenPhase[] = ['KT_GO', 'AV_ERSTELLEN', 'UNTERSCHRIFT'];

/** Bedarf-Status, ab denen der Kostentraeger zugestimmt hat. */
const BEDARF_KT_GO_ERTEILT = ['ONBOARDING', 'GEWONNEN'];

/**
 * In welcher Einstellen-Spalte ein ONBOARDING-Bewerber steht. Der versendete
 * AV ist eine Tatsache und sticht den Bedarf-Status -- sonst hinge eine Karte,
 * deren Bedarf nie auf Onboarding gezogen wurde, fuer immer in Phase 1. Ohne
 * zugeordneten Bedarf bleibt sie dort: dann fehlt erst die Zuordnung.
 */
export const einstellenPhase = (
  bewerber: Pick<Bewerber, 'onboardingUnterlagenGesendet' | 'zugeordneteBedarfe'>,
): EinstellenPhase => {
  if (bewerber.onboardingUnterlagenGesendet) return 'UNTERSCHRIFT';
  const bedarf = bewerber.zugeordneteBedarfe?.[0];
  if (bedarf && BEDARF_KT_GO_ERTEILT.includes(bedarf.status)) return 'AV_ERSTELLEN';
  return 'KT_GO';
};

// Offboarding checklist steps. Unlike the onboarding list these gate nothing —
// they document how the employment was wound up — so there is deliberately no
// "all done" helper to go with them.
export interface OffboardingStep {
  field: keyof Pick<
    Bewerber,
    | 'offboardingElternKommuniziert'
    | 'offboardingKostentraegerKommuniziert'
    | 'offboardingKuendigungVersendet'
    | 'offboardingAufhebungsvertragVersendet'
    | 'offboardingWegfallZweckVersendet'
    | 'offboardingBestaetigungAbgelegt'
    | 'offboardingStundenGeklaert'
    | 'offboardingKilankaOffboarding'
    | 'offboardingMitarbeiterAbgemeldet'
  >;
  label: string;
  /** Shown as an info tooltip where the label alone leaves the step ambiguous. */
  description?: string;
}

export const OFFBOARDING_STEPS: OffboardingStep[] = [
  { field: 'offboardingElternKommuniziert', label: 'Eltern kommuniziert' },
  { field: 'offboardingKostentraegerKommuniziert', label: 'Kostenträger kommuniziert' },
  { field: 'offboardingKuendigungVersendet', label: 'Kündigung versendet' },
  { field: 'offboardingAufhebungsvertragVersendet', label: 'Aufhebungsvertrag versendet' },
  { field: 'offboardingWegfallZweckVersendet', label: 'Wegfall Zweck versendet' },
  {
    field: 'offboardingBestaetigungAbgelegt',
    label: 'Bestätigung abgelegt',
    description:
      'Einlieferung bei Kündigung und Wegfall Zweck — oder der unterschriebene Aufhebungsvertrag.',
  },
  { field: 'offboardingStundenGeklaert', label: 'Stunden geklärt' },
  { field: 'offboardingKilankaOffboarding', label: 'Kilanka Offboarding' },
  { field: 'offboardingMitarbeiterAbgemeldet', label: 'Mitarbeiter abgemeldet' },
];

// True when at least one offboarding step is ticked. Drives whether a VERLOREN
// Bewerber shows the list at all: an ordinary Absage never touched it, and an
// empty nine-row checklist there would be noise.
export const hasAnyOffboardingStep = (bewerber: Bewerber): boolean =>
  OFFBOARDING_STEPS.some((step) => bewerber[step.field]);

// Document type display configuration
export const DokumentTypConfig: Record<DokumentTyp, { label: string }> = {
  [DokumentTyp.LEBENSLAUF]: { label: 'Lebenslauf' },
  [DokumentTyp.ZEUGNIS]: { label: 'Zeugnis' },
  [DokumentTyp.FUEHRUNGSZEUGNIS]: { label: 'Führungszeugnis' },
  [DokumentTyp.QUALIFIKATION]: { label: 'Qualifikation' },
  [DokumentTyp.SONSTIGES]: { label: 'Sonstiges' },
};

// Previewable mime types (PDF and images)
export const PREVIEWABLE_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
] as const;

// Helper to check if document can be previewed
export const isDocumentPreviewable = (mimeType: string): boolean => {
  return PREVIEWABLE_MIME_TYPES.includes(mimeType as typeof PREVIEWABLE_MIME_TYPES[number]);
};

// Helper to check if mime type is an image
export const isImageMimeType = (mimeType: string): boolean => {
  return mimeType.startsWith('image/');
};

// Helper to check if mime type is a PDF
export const isPdfMimeType = (mimeType: string): boolean => {
  return mimeType === 'application/pdf';
};

// Absage (rejection) reason enum
export enum AbsageGrund {
  DURCH_BEWERBER = 'DURCH_BEWERBER',
  REGULAERE_ABSAGE = 'REGULAERE_ABSAGE',
}

// Absage Bewerber DTO
export interface AbsageBewerberDto {
  grund: AbsageGrund;
  bemerkung?: string;
  emailBodyHtml?: string;
  emailSubject?: string;
}

// Nicht geeignet predefined reasons
export const NICHT_GEEIGNET_REASONS = [
  'Verständigung zu schwierig',
  'Gehalt passt nicht',
  'Zeiten passen nicht',
  'Nicht erreichbar',
  'Kein Bedarf',
  'Fehlende Eignung',
] as const;

// Nicht geeignet DTO
export interface NichtGeeignetDto {
  selectedReasons?: string[];
  freitextGrund?: string;
}

// Hard delete Bewerber DTO
export interface HardDeleteBewerberDto {
  reason?: string;
}

// Absage response with email status
export interface AbsageResponse {
  success: boolean;
  data: Bewerber;
  meta: {
    emailSent: boolean;
    emailSkipReason?: string;
  };
}

// ---------------------------------------------------------------------------
// Bedarfe within reach (Vorstellungsgespräch section)
// ---------------------------------------------------------------------------

/** What does not fit about a Bedarf. Built on the backend so the wording lives once. */
export type BedarfFitIssueKind =
  | 'DISTANCE'
  | 'TIME_EARLY'
  | 'TIME_LATE'
  | 'ROUTE_TIME'
  | 'HOURS_LOW'
  | 'HOURS_HIGH';

export interface BedarfFitIssue {
  kind: BedarfFitIssueKind;
  severity: 'WARN';
  text: string;
}

/**
 * Straight-line kilometres beyond which no travel time is offered at all.
 * Mirrors MAX_ROUTE_DISTANCE_KM in backend/src/modules/geo/geo.constants.ts —
 * the backend rejects such a request, so the button must not offer it.
 */
export const MAX_ROUTE_DISTANCE_KM = 40;

/**
 * How many hours a week a Bewerber wants to work, as one short phrase:
 * "15–25 Std./Woche", "ab 15 …", "bis 25 …". `null` when nothing was entered,
 * so the caller can leave the field out instead of printing a dash.
 */
export function formatWochenstundenWunsch(
  min: number | null | undefined,
  max: number | null | undefined,
): string | null {
  const hatMin = min != null;
  const hatMax = max != null;
  if (hatMin && hatMax) return min === max ? `${min} Std./Woche` : `${min}–${max} Std./Woche`;
  if (hatMin) return `ab ${min} Std./Woche`;
  if (hatMax) return `bis ${max} Std./Woche`;
  return null;
}

export interface MatchingBedarf {
  id: string;
  childFirstName: string;
  childLastName: string | null;
  schoolName: string | null;
  schoolCity: string | null;
  status: BedarfStatus;
  priority: string;
  distanceKm: number;
  geoPrecision: GeoPrecision;
  /** Empty means a clean match. */
  issues: BedarfFitIssue[];
  weeklyHours: number;
  times: Array<{ day: string; start: string; end: string }>;
  startDateRequested: string | null;
  /**
   * Filled by the local routing container for the whole list. Null only when the
   * pair is too far apart to route, lies outside the local graph, or the
   * container was unreachable — those rows still offer the button.
   */
  routeDistanceKm: number | null;
  routeDurationMin: number | null;
}

export interface MatchingBedarfMeta {
  radiusKm: number;
  /** The commute limit the evaluation used, wherever it came from. */
  maxPendelKm: number | null;
  total: number;
  /** Bedarfe dropped because they carry no schedule at all. */
  hiddenNoTimes: number;
  /** Bedarfe dropped because the driving time is well over the stated maximum. */
  hiddenRouteTime: number;
  /** False while no time window has been stated — the time rule is then off. */
  zeitfensterActive: boolean;
  bewerberGeocoded: boolean;
  bewerberPrecision: GeoPrecision;
}

/** Own response shape, like the Bedarf-side list: `meta` is not pagination. */
export interface MatchingBedarfeResponse {
  success: boolean;
  data: MatchingBedarf[];
  meta: MatchingBedarfMeta;
}

/** Criteria taken from the running interview draft. */
export interface MatchingBedarfeParams {
  zeitVon?: string;
  zeitBis?: string;
  maxPendelKm?: number;
  maxPendelMinuten?: number;
  minWochenstunden?: number;
  maxWochenstunden?: number;
  limit?: number;
}

export const MATCHING_BEDARFE_PAGE_SIZE = 50;
